// apps/client/src/api/http.ts
import { API_ERROR_CODES, type ApiErrorCode } from '@whiskerton/shared';
import { API_BASE } from '../boot/env';
import { clearToken, getToken } from './token';

export class HttpError extends Error {
  readonly status: number;
  readonly error: ApiErrorCode;
  constructor(status: number, error: ApiErrorCode, message?: string) { super(message ?? error); this.status = status; this.error = error; }
}
const listeners = new Set<() => void>();
export function onUnauthorized(fn: () => void): () => void { listeners.add(fn); return () => { listeners.delete(fn); }; }
const byStatus = (s: number): ApiErrorCode =>
  s === 401 ? 'unauthorized' : s === 403 ? 'forbidden' : s === 404 ? 'not_found' : s === 429 ? 'rate_limited' : s >= 400 && s < 500 ? 'invalid' : 'server_error';
type Opts = { auth?: boolean; credentials?: RequestCredentials };
export async function requestRaw(method: string, path: string, body?: unknown, opts: Opts = {}): Promise<Response> {
  const headers = new Headers();
  if (body !== undefined) headers.set('content-type', 'application/json');
  const token = opts.auth === false ? null : getToken();
  if (token) headers.set('authorization', `Bearer ${token}`);
  const res = await fetch(API_BASE + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: opts.credentials ?? 'same-origin' });
  if (res.ok) return res;
  let error: ApiErrorCode = byStatus(res.status), message: string | undefined;
  try {
    const j = (await res.clone().json()) as { error?: string; message?: string } | null;
    if (j && typeof j.error === 'string' && (API_ERROR_CODES as readonly string[]).includes(j.error)) error = j.error as ApiErrorCode;
    if (j && typeof j.message === 'string') message = j.message;
  } catch { /* not JSON */ }
  if (res.status === 401) { clearToken(); for (const fn of listeners) fn(); }
  throw new HttpError(res.status, error, message);
}
export async function request<T>(method: string, path: string, body?: unknown, opts?: Opts): Promise<T> {
  const res = await requestRaw(method, path, body, opts);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}
